import { Link, useFetcher, useLoaderData, useNavigate, useRevalidator } from 'react-router';
import { useEffect, useState } from 'react';
import { generateAddLiquidityManifest } from '~/lib/manifests';
import { ROUND_UP, classNames, getIconUrl, onIconError } from '~/lib/util';
import { LoaderFunctionArgs, redirect } from 'react-router';
import AccountSelector from '~/components/account-selector';
import { useRadix } from '~/rdt/radixProvider';
import TransactionButton from '~/components/transaction-button';
import { getPair, getPairByBaseToken } from '~/lib/models/pair';
import { SelectToken, getToken } from '~/lib/models/tokens';
import { Account } from '@radixdlt/radix-dapp-toolkit';
import { loader as pairsLoader } from '~/routes/cronjob.new-pairs';
import TokenRow from '~/components/token-row';
import { loader as walletLoader } from '~/routes/wallet.$account';
import { fetchPairState, useGatewayApi } from '~/rdt/gateway';
import BigNumber from 'bignumber.js';
import { usePrevious } from '~/hooks/usePrevious';
import { IGNITION_DEX_COMPONENT_ADDRESS } from '~/lib/ignition';
import { setDbFromContext } from '~/lib/db';
import { trackEvent } from "~/lib/analytics";
import { InfoPanel } from '~/components/liquidity/info-panel';
import { fetchRewardPools } from '~/lib/radix-rewards';
import { PositionData } from '~/components/liquidity-position-row';

export async function loader({ request, params, context }: LoaderFunctionArgs) {
  setDbFromContext(context);

  if (!params.componentAddress) {
    throw redirect('/liquidity/add');
  }

  let direction = 'base';

  const url = new URL(request.url);
  const dir = url.searchParams.get('direction');

  if (dir == 'quote') {
    direction = 'quote';
  }

  let isBase = direction == 'base';
  let inShortage = false;
  let ratio = '0';

  // always check for new pairs first
  await pairsLoader({ context } as any);

  // check if we have a component address or the old basetoken address
  const componentAddress = params.componentAddress;

  let row;

  if (componentAddress.startsWith('component_')) {
    const [pairRow] = await getPair(componentAddress);
    row = pairRow;
  } else {
    const [pairRow] = await getPairByBaseToken(componentAddress);
    row = pairRow;
  }

  if (!row) {
    return ({
      pair: null,
      tokens: [],
      isBase: false,
      inShortage: false,
      ratio: 0,
      error: 'Pair not found.',
      transactions: [],
      basePoolAmounts: { base: 0, quote: 0 },
      quotePoolAmounts: { base: 0, quote: 0 },
      next_cursor: null,
    });
  }

  const { pairState, basePoolState, quotePoolState } = await fetchPairState(row.Pair);

  if (!pairState || !basePoolState || !quotePoolState) {
    return ({
      pair: null,
      tokens: [],
      isBase: false,
      inShortage,
      ratio,
      error: 'Could not fetch pair state.',
      transactions: [],
      basePoolAmounts: { base: 0, quote: 0 },
      quotePoolAmounts: { base: 0, quote: 0 },
      next_cursor: null,
    });
  }

  if (isBase && pairState.shortage == 'BaseShortage') {
    inShortage = true;

    ratio = new BigNumber(basePoolState.vaults[1].amount).div(basePoolState.vaults[0].amount).toFixed();
  }
  else if (!isBase && pairState.shortage == 'QuoteShortage') {
    inShortage = true;

    ratio = new BigNumber(quotePoolState.vaults[1].amount).div(quotePoolState.vaults[0].amount).toFixed();
  }

  const basePoolAmounts = {
    base: basePoolState.vaults[0].amount || 0,
    quote: basePoolState.vaults[1].amount || 0
  };
  const quotePoolAmounts = {
    quote: quotePoolState.vaults[0].amount || 0,
    base: quotePoolState.vaults[1].amount || 0
  };

  const [quoteToken] = await getToken(row.Pair.quoteToken);
  const rewardPairs = await fetchRewardPools();

  // Fetch recent transactions
  const { stream } = useGatewayApi;
  const ticker_id = row.Pair.address;
  let buys = [];
  let sells = [];
  let transactions = [];
  let next_cursor = null;

  try {
    let query = {
      "limit_per_page": 10,
      "kind_filter": "All" as any,
      "affected_global_entities_filter": [ticker_id],
      "opt_ins": {
        "receipt_events": true
      },
      "order": "Desc" as any
    }

    // @ts-ignore
    const result = await stream.innerClient.streamTransactions({
      streamTransactionsRequest: query
    });

    next_cursor = result.next_cursor;

    for (let transaction of result.items) {
      if (transaction.transaction_status !== 'CommittedSuccess') {
        continue;
      }

      for (let event of transaction.receipt?.events || []) {
        if (event.name !== 'SwapEvent') {
          continue;
        }

        const componentAddress = (event as any).emitter.entity.entity_address;
        if (componentAddress !== ticker_id) {
          continue;
        }

        let baseAmount = 0, quoteAmount = 0;

        for (let field of (event as any).data.fields) {
          if (field.field_name == 'base_amount') {
            baseAmount = parseFloat(field.value);
          }

          if (field.field_name == 'quote_amount') {
            quoteAmount = parseFloat(field.value);
          }
        }

        const swap = {
          trade_id: transaction.intent_hash,
          price: Math.abs(quoteAmount / baseAmount),
          base_volume: Math.abs(baseAmount),
          quote_volume: Math.abs(quoteAmount),
          trade_timestamp: new Date(transaction.confirmed_at || 0).getTime(),
          type: (baseAmount > 0 ? 'buy' : 'sell') as "buy" | "sell"
        };

        transactions.push(swap);
      }
    }
  } catch (e) {
    console.error("Failed to fetch transactions", e);
  }

  return ({
    pair: row.Pair,
    tokens: [row.Token, quoteToken],
    isBase,
    inShortage,
    ratio,
    error: null,
    transactions,
    basePoolAmounts,
    quotePoolAmounts,
    next_cursor,
    rewardPairs
  });
}

export default function AddLiquidityDetail() {
  const { pair, tokens, isBase, inShortage, ratio, error, transactions: initialTransactions, basePoolAmounts, quotePoolAmounts, next_cursor: initialNextCursor, rewardPairs } = useLoaderData<typeof loader>();
  const { sendTransaction } = useRadix();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const walletFetcher = useFetcher<typeof walletLoader>();
  const transactionsFetcher = useFetcher();

  const [account, setAccount] = useState<Account | null>(null);
  const [inputAmount, setInputAmount] = useState<string>('');
  const [coInputAmount, setCoInputAmount] = useState<string>('');
  const [balance, setBalance] = useState<string>('');
  const [coBalance, setCoBalance] = useState<string>('');

  const [inputToken, setInputToken] = useState<SelectToken>(tokens[isBase ? 0 : 1] as any);
  const [coToken, setCoToken] = useState<SelectToken>(tokens[isBase ? 1 : 0] as any);

  const [warning, setWarning] = useState<string | null>();

  const [transactions, setTransactions] = useState(initialTransactions);
  const [nextCursor, setNextCursor] = useState(initialNextCursor);
  const [userPositions, setUserPositions] = useState<PositionData[]>([]);

  const previousIsBase = usePrevious(isBase);
  const previousTokens = usePrevious(tokens);

  useEffect(() => {
    setTransactions(initialTransactions);
    setNextCursor(initialNextCursor);
  }, [initialTransactions, initialNextCursor]);

  useEffect(() => {
    if (transactionsFetcher.data) {
      const data = transactionsFetcher.data as any;

      if (data.transactions) {
        setTransactions((prev) => [...prev, ...data.transactions]);
        setNextCursor(data.next_cursor);
      }
    }
  }, [transactionsFetcher.data]);

  useEffect(() => {
    if (!walletFetcher.data || !pair || !basePoolAmounts || !quotePoolAmounts) {
      setUserPositions([]);
      return;
    }

    const walletTokens = walletFetcher.data as any[];
    const baseLP = walletTokens.find((t) => t.address === pair.baseLPToken);
    const quoteLP = walletTokens.find((t) => t.address === pair.quoteLPToken);

    let positions: PositionData[] = [];

    if (baseLP) {
      const share = parseFloat(baseLP.amount) / parseFloat(baseLP.totalSupply);
      const baseAmount = share * basePoolAmounts.base;
      const quoteAmount = share * basePoolAmounts.quote;
      const basePrice = tokens[0].lastPrice || 0;
      const quotePrice = tokens[1].lastPrice || 0;
      const usdValue = baseAmount * basePrice + quoteAmount * quotePrice;

      positions.push({
        pair: pair,
        baseToken: tokens[0],
        quoteToken: tokens[1],
        lpToken: baseLP,
        poolState: { vaults: [{ amount: basePoolAmounts.base }, { amount: basePoolAmounts.quote }] }, // Mocking poolState structure expected by LiquidityPositionRow
        isBase: true,
        amount: baseLP.amount,
        usdValue,
        share,
        radixRewards: rewardPairs.includes(pair.address)
      });
    }

    if (quoteLP) {
      const share = parseFloat(quoteLP.amount) / parseFloat(quoteLP.totalSupply);
      const baseAmount = share * quotePoolAmounts.base;
      const quoteAmount = share * quotePoolAmounts.quote;
      const basePrice = tokens[0].lastPrice || 0;
      const quotePrice = tokens[1].lastPrice || 0;
      const usdValue = baseAmount * basePrice + quoteAmount * quotePrice;

      positions.push({
        pair: pair,
        baseToken: tokens[0],
        quoteToken: tokens[1],
        lpToken: quoteLP,
        poolState: { vaults: [{ amount: quotePoolAmounts.quote }, { amount: quotePoolAmounts.base }] }, // Mocking poolState structure expected by LiquidityPositionRow. Note: Quote pool vaults might be flipped or not, need to check how LiquidityPositionRow uses it.
        // LiquidityPositionRow uses: share * (row.isBase ? row.poolState.vaults[0].amount : row.poolState.vaults[1].amount) for base token
        // If isBase is false (Quote LP):
        // Base Token amount: share * row.poolState.vaults[1].amount
        // Quote Token amount: share * row.poolState.vaults[0].amount
        // So for Quote LP, vaults[0] should be Quote amount, vaults[1] should be Base amount.
        isBase: false,
        amount: quoteLP.amount,
        usdValue,
        share,
        radixRewards: rewardPairs.includes(pair.address)
      });
    }

    setUserPositions(positions);

  }, [walletFetcher.data, pair, basePoolAmounts, quotePoolAmounts, tokens, isBase, rewardPairs]);

  useEffect(() => {
    if (transactionsFetcher.data) {
      const data = transactionsFetcher.data as any;

      if (data.transactions) {
        setTransactions((prev) => [...prev, ...data.transactions]);
        setNextCursor(data.next_cursor);
      }
    }
  }, [transactionsFetcher.data]);

  function onLoadMore() {
    if (nextCursor && pair) {
      transactionsFetcher.load(`/api/liquidity/transactions?ticker_id=${pair.address}&cursor=${nextCursor}`);
    }
  }

  useEffect(() => {
    if (!walletFetcher.data) {
      return;
    }

    setBalance(walletFetcher.data?.filter((b) => b.address == inputToken.address)[0]?.amount || '0');
    setCoBalance(walletFetcher.data?.filter((b) => b.address == coToken.address)[0]?.amount || '0');
  }, [walletFetcher.data]);

  useEffect(() => {
    // Only run on change not on set
    if (previousIsBase === undefined) {
      return;
    }

    setWarning('');
    setInputAmount('');
    setCoInputAmount('');
    setInputToken(tokens[isBase ? 0 : 1] as any);
    setCoToken(tokens[isBase ? 1 : 0] as any);

    if (account) {
      walletFetcher.load(`/wallet/${account.address}`);
    }
  }, [isBase]);

  useEffect(() => {
    // Only run on change not on set
    if (previousTokens === undefined) {
      return;
    }

    setWarning('');
    setInputAmount('');
    setCoInputAmount('');
    setInputToken(tokens[isBase ? 0 : 1] as any);
    setCoToken(tokens[isBase ? 1 : 0] as any);

    if (account) {
      walletFetcher.load(`/wallet/${account.address}`);
    }
  }, [tokens]);

  function onInputChange(value: string) {
    setWarning('');

    if (value == '') {
      setInputAmount(value);
      setCoInputAmount('');
      return;
    }

    console.log('ratio', ratio)

    let bigValue = new BigNumber(value)
    let coInput = bigValue.times(ratio);

    // Maximize the adding if there is not enough coBalance
    if (coInput.gt(coBalance)) {
      const bigCobalance = new BigNumber(coBalance);

      setWarning(
        `You need at least ${Math.round(coInput.toNumber() * 100000) / 100000} ${coToken.symbol} to add ${Math.round(bigValue.toNumber() * 100000) / 100000} ${inputToken.symbol}.
            The maximum you can add is ${Math.round(bigCobalance.div(ratio).toNumber() * 100000) / 100000} ${inputToken.symbol}`
      );

      bigValue = bigCobalance.div(ratio);
      coInput = bigCobalance;
    }

    setInputAmount(bigValue.toFixed((inputToken as any).divisibility ?? 18));
    setCoInputAmount(coInput.toFixed((coToken as any).divisibility ?? 18, ROUND_UP));
  }

  function onInputToken(token: SelectToken) {
    let direction = 'base';

    if (token.address === tokens[1]?.address) {
      direction = 'quote';
    }

    navigate(`../${pair?.address}?direction=${direction}`);
  }

  function setRelativeInputAmount(input: string) {
    onInputChange(new BigNumber(balance).times(input).toFixed((inputToken as any).divisibility ?? 18, ROUND_UP));
  }

  async function onAddLiquidity() {
    if (!account) {
      return;
    }

    if (!pair) {
      return;
    }

    const tokenAddress = tokens[isBase ? 0 : 1].address;
    const coTokenAddress = tokens[isBase ? 1 : 0].address;
    const tokenSymbol = tokens[isBase ? 0 : 1].symbol;

    const transactionManifest = generateAddLiquidityManifest(account.address, tokenAddress, inputAmount, coTokenAddress, coInputAmount, pair);

    await sendTransaction(transactionManifest, `Add ${tokenSymbol} as liquidity to DefiPlaza`);

    trackEvent("send_tx", {
      type: "addLiquidity",
    });

    revalidator.revalidate();
    onInputChange('');
    walletFetcher.load(`/wallet/${account.address}`);
  }

  function onAccount(account: Account) {
    setAccount(account);

    walletFetcher.load(`/wallet/${account.address}`);
  }

  return (
    <div className="flex flex-col lg:flex-row lg:space-x-8">
      {pair && tokens.length === 2 ? (
        <InfoPanel
          pair={pair}
          baseToken={tokens[0]}
          quoteToken={tokens[1]}
          transactions={transactions as any}
          basePoolAmounts={basePoolAmounts}
          quotePoolAmounts={quotePoolAmounts}
          onLoadMore={onLoadMore}
          hasMore={!!nextCursor}
          isLoadingMore={transactionsFetcher.state === 'loading'}
          userPositions={userPositions}
          isIncentivized={rewardPairs.includes(pair.address)}
        />
      ) : null}

      <div className="flex flex-col space-y-4 lg:w-1/2">
        {error ? <p className="shadow-xs text-md mt-2 rounded-md bg-red-800 px-4 py-1 text-center text-base font-medium text-white">{error}</p> : null}

        {!error ? (
          <>
            <h2 className="mx-4 mt-8 text-3xl font-bold lg:hidden">Add Liquidity</h2>
            <h2 className="hidden text-3xl font-bold text-gray-900 dark:text-white lg:block">Add Liquidity</h2>
            <AccountSelector mode="light" onAccount={onAccount}></AccountSelector>

            <section className="flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
              <label htmlFor="tokenInput" className="text-sm text-purple-800">
                Token
              </label>
              <span className="isolate inline-flex rounded-md">
                <button
                  type="button"
                  onClick={() => onInputToken(tokens[0] as any)}
                  className={classNames(isBase ? "ring-primary-500 ring-2" : "ring-1 ring-gray-300", "relative grow rounded-l-md bg-white px-3 py-2 ring-inset hover:bg-gray-50 focus:z-10")}
                >
                  <TokenRow token={tokens[0] as any} />
                </button>

                <button
                  type="button"
                  onClick={() => onInputToken(tokens[1] as any)}
                  className={classNames(!isBase ? "ring-primary-500 ring-2" : "ring-1 ring-gray-300", "relative grow rounded-r-md bg-white px-3 py-2 ring-inset hover:bg-gray-50 focus:z-10")}
                >
                  <TokenRow token={tokens[1] as any} />
                </button>
              </span>
            </section>

            <section className="flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
              <div className="flex items-center justify-between">
                <label htmlFor="tokenInput" className="text-sm text-purple-800">
                  From
                </label>
                {walletFetcher.data ? (
                  <span className="w-60 truncate text-right text-sm font-normal text-black lg:text-right">Balance: {(Math.round((+(balance || 0)) * 1000000) / 1000000).toLocaleString()}</span>
                ) : null}
              </div>
              <div className="flex items-center justify-between space-x-4">
                <div className="hover:text-primary-500 focus:outline-3 focus:ring-primary-200 focus:border-primary-300 w-40 flex-none rounded-md py-2 text-left text-black focus:ring-1 sm:text-sm">
                  <span className="flex items-center">
                    <img src={getIconUrl(inputToken.iconUrl)} onError={(evt) => onIconError(evt)} alt="" className="h-8 w-8 shrink-0 rounded-full" />
                    <span className="ml-3 block text-xl font-medium">{inputToken.symbol}</span>
                    {/* <span className="ml-3 text-xs text-gray-500 truncate">{token.name}</span> */}
                  </span>
                </div>
                <input
                  type="text"
                  value={inputAmount ? parseFloat(inputAmount) : ""}
                  onChange={(evt) => onInputChange(evt.target.value)}
                  placeholder="0.00"
                  autoComplete="off"
                  spellCheck="false"
                  className="m-0 w-40 flex-auto border-none bg-transparent p-0 text-right text-4xl text-black placeholder:text-purple-100 dark:placeholder:text-purple-800/80"
                />
              </div>
              {walletFetcher.data && +(balance || 0) > 0 ? (
                <>
                  <p className="text-primary-500 mt-6 text-right text-sm font-normal">{Math.round((+inputAmount / +(balance || 1)) * 100)}%</p>
                  <input
                    id="steps-range"
                    type="range"
                    min="0"
                    max="1"
                    value={new BigNumber(inputAmount || 0).div(balance || 1).toFixed((inputToken as any).divisibility ?? 18, ROUND_UP) || "0"}
                    onChange={(evt: React.ChangeEvent<HTMLInputElement>) => setRelativeInputAmount((evt.target as HTMLInputElement).value)}
                    step="0.01"
                    className="bg-primary-100 ring-primary-300 mb-3 mt-2 h-2 w-full cursor-pointer appearance-none rounded-lg"
                  />

                  <div className="flex w-full items-center justify-between space-x-1">
                    <button
                      type="button"
                      onClick={() => setRelativeInputAmount("0")}
                      className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                    >
                      0%
                    </button>
                    <button
                      type="button"
                      onClick={() => setRelativeInputAmount("0.25")}
                      className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                    >
                      25%
                    </button>
                    <button
                      type="button"
                      onClick={() => setRelativeInputAmount("0.5")}
                      className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                    >
                      50%
                    </button>
                    <button
                      type="button"
                      onClick={() => setRelativeInputAmount("0.75")}
                      className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                    >
                      75%
                    </button>
                    <button
                      type="button"
                      onClick={() => setRelativeInputAmount("1")}
                      className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                    >
                      100%
                    </button>
                  </div>
                </>
              ) : null}
            </section>

            {warning ? <p className="shadow-xs text-md mt-2 rounded-md bg-red-800 px-4 py-1 text-center text-base font-medium text-white">{warning}</p> : null}

            {inShortage ? (
              <section className="flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
                <div className="flex items-center justify-between">
                  <label htmlFor="tokenInput" className="text-sm text-purple-800">
                    From
                  </label>
                  {walletFetcher.data ? (
                    <span className="w-60 truncate text-right text-sm font-normal text-black lg:text-right">Balance: {(Math.round(+(coBalance || 0) * 1000000) / 1000000).toLocaleString()}</span>
                  ) : null}
                </div>
                <div className="flex items-center justify-start">
                  <div className="hover:text-primary-500 focus:outline-3 focus:ring-primary-200 focus:border-primary-300 w-40 flex-none rounded-md py-2 text-left text-black focus:ring-1 sm:text-sm">
                    <span className="flex items-center">
                      <img src={getIconUrl(coToken.iconUrl)} alt="" className="h-8 w-8 shrink-0 rounded-full" />
                      <span className="ml-3 block text-xl font-medium">{coToken.symbol}</span>
                      {/* <span className="ml-3 text-xs text-gray-500 truncate">{token.name}</span> */}
                    </span>
                  </div>
                  <input
                    type="text"
                    value={coInputAmount ? parseFloat(coInputAmount) : ""}
                    onChange={() => { }}
                    placeholder="0.00"
                    autoComplete="off"
                    spellCheck="false"
                    className="w-40 flex-auto border-none bg-transparent p-0 text-right text-4xl text-black placeholder:text-purple-100 dark:placeholder:text-purple-800/80"
                  />
                </div>

                <p className="text-xs text-gray-800">
                  {inputToken.symbol} is in shortage, so an amount of {coToken.symbol} also needs to be provided.{" "}
                  <a href="https://docs.defiplaza.net/defiplaza/radix/providing-liquidity/add-liquidity" target="_blank" className="text-primary-500">
                    Read more
                  </a>
                  .
                </p>
              </section>
            ) : null}

            <TransactionButton title="Add Liquidity" onClick={onAddLiquidity} />
          </>
        ) : null}
      </div>
    </div>
  );
}
